/**
 * The event's tracked athletes for its map (docs/features/athletes-and-trackers.md, Viewers): who they are, fetched
 * now and then, and where they are, polled while the map is on screen. The two are read apart because the roster
 * changes rarely and positions every few seconds; the API caches both per event, so the cost doesn't grow with the
 * audience. Nothing is polled for an event with no athletes, and nothing at all while the page is hidden.
 */

import { useEffect, useMemo, useState } from 'react'
import { apiUrl } from '@/lib/apiBase'
import { noteServerTime } from './serverClock'

export interface EventAthlete {
  id: string
  name: string
  bib: string | null
  gender: 'female' | 'male' | null
  /** Their race: one of the event's courses. */
  routeId: string | null
  photoUrl: string | null
}

export interface AthletePosition {
  latitude: number
  longitude: number
  /** When the fix was taken, in epoch milliseconds. */
  recordedAt: number
}

export interface EventAthletes {
  roster: EventAthlete[]
  positions: ReadonlyMap<string, AthletePosition>
  /** The server's time when it read the positions, in epoch milliseconds (0 before the first read): ages are
   *  measured from it, so a viewer's wrong clock can't make every dot stale. */
  at: number
  /** Older than this, a fix is drawn as stale. */
  staleAfterMs: number
}

export const POSITIONS_EVERY_MS = 10_000
export const ROSTER_EVERY_MS = 60_000
/** Older than this a fix is drawn as stale, unless the server says otherwise (the API's STALE_AFTER). */
export const DEFAULT_STALE_AFTER_MS = 5 * 60_000

const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value : null)

export function parseRoster(body: unknown): EventAthlete[] {
  const rows = (body as { data?: unknown } | null)?.data
  if (!Array.isArray(rows)) return []
  return rows.flatMap((row) => {
    const id = text(row?.id)
    const name = text(row?.name)
    if (!id || !name) return []
    const gender = row.gender === 'female' || row.gender === 'male' ? row.gender : null
    return [{ id, name, bib: text(row.bib), gender, routeId: text(row.route_id), photoUrl: text(row.photo_url) }]
  })
}

export function parsePositions(body: unknown): Omit<EventAthletes, 'roster'> {
  const data = (body as { data?: { at?: unknown; positions?: unknown; stale_after_s?: unknown } } | null)?.data
  const positions = new Map<string, AthletePosition>()
  for (const row of Array.isArray(data?.positions) ? data.positions : []) {
    if (!Array.isArray(row)) continue
    const [id, latitude, longitude, recordedAt] = row
    if (typeof id !== 'string' || ![latitude, longitude, recordedAt].every((n) => typeof n === 'number' && Number.isFinite(n))) continue
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) continue
    positions.set(id, { latitude, longitude, recordedAt: recordedAt * 1000 })
  }
  const staleAfter = data?.stale_after_s
  const at = typeof data?.at === 'string' ? Date.parse(data.at) : NaN
  return {
    positions,
    at: Number.isFinite(at) ? at : Date.now(),
    staleAfterMs: typeof staleAfter === 'number' && staleAfter > 0 ? staleAfter * 1000 : DEFAULT_STALE_AFTER_MS,
  }
}

/** A public read's `data.at`, the server's time when it built the answer, in epoch milliseconds (NaN without one). */
export function serverAt(body: unknown): number {
  const at = (body as { data?: { at?: unknown } } | null)?.data?.at
  return typeof at === 'string' ? Date.parse(at) : Number.NaN
}

/** A public read that failed with an answer (`status`), as opposed to no answer (a network failure, an abort). */
export class PublicReadError extends Error {
  constructor(
    readonly path: string,
    readonly status: number,
  ) {
    super(`${path}: ${status}`)
  }
}

/**
 * A public endpoint's JSON, read without credentials so the API's and the CDN's caches serve every viewer alike, and
 * revalidated each time (the API answers 304 when nothing changed).
 */
export async function getJson(path: string, signal?: AbortSignal): Promise<unknown> {
  const res = await fetch(apiUrl(path), {
    credentials: 'omit',
    // Revalidated each time: the API answers 304 when nothing changed.
    cache: 'no-cache',
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!res.ok) throw new PublicReadError(path, res.status)
  return res.json()
}

/** Runs `tick` now and every `everyMs` while the page is visible, and again the moment it shows. */
export function poll(everyMs: number, tick: () => void): () => void {
  let timer = 0
  const schedule = () => {
    window.clearTimeout(timer)
    timer = window.setTimeout(run, everyMs)
  }
  const run = () => {
    if (document.visibilityState !== 'hidden') tick()
    schedule()
  }
  const onVisibility = () => {
    if (document.visibilityState === 'visible') run()
  }
  document.addEventListener('visibilitychange', onVisibility)
  run()
  return () => {
    window.clearTimeout(timer)
    document.removeEventListener('visibilitychange', onVisibility)
  }
}

const NO_POSITIONS: Omit<EventAthletes, 'roster'> = { positions: new Map(), at: 0, staleAfterMs: DEFAULT_STALE_AFTER_MS }

/**
 * The event's athletes and their newest positions while `enabled` (the map is shown). A failed read keeps what was
 * there and tries again on the next tick. `rosterWanted` also keeps the roster (without positions) while the map is out
 * of sight: a stage's broadcast elements and the host's console name and picture athletes from it.
 */
export function useEventAthletes(eventId: string | null | undefined, enabled: boolean, rosterWanted = false): EventAthletes {
  const [roster, setRoster] = useState<EventAthlete[]>([])
  const [latest, setLatest] = useState<Omit<EventAthletes, 'roster'>>(NO_POSITIONS)

  useEffect(() => {
    setRoster([])
    setLatest(NO_POSITIONS)
  }, [eventId])

  const rosterOn = enabled || rosterWanted
  useEffect(() => {
    if (!eventId || !rosterOn) return
    const controller = new AbortController()
    const stop = poll(ROSTER_EVERY_MS, () => {
      getJson(`public/events/${encodeURIComponent(eventId)}/athletes`, controller.signal)
        .then((body) => setRoster(parseRoster(body)))
        .catch(() => {})
    })
    return () => {
      stop()
      controller.abort()
    }
  }, [eventId, rosterOn])

  const tracking = roster.length > 0
  useEffect(() => {
    if (!eventId || !enabled || !tracking) return
    const controller = new AbortController()
    const stop = poll(POSITIONS_EVERY_MS, () => {
      getJson(`public/events/${encodeURIComponent(eventId)}/athletes/positions`, controller.signal)
        .then((body) => {
          noteServerTime(serverAt(body))
          setLatest(parsePositions(body))
        })
        .catch(() => {})
    })
    return () => {
      stop()
      controller.abort()
    }
  }, [eventId, enabled, tracking])

  // The same object while neither read changes, so a memoised stage under the page passes the page's other renders by.
  return useMemo(() => ({ roster, ...latest }), [roster, latest])
}
